import { Events } from 'src/constants/webSocketConsts/events';
import { ensureUnreachable } from 'src/utils/common_utils';

interface ListCacheOptions<T> {
  /** Field that identifies a cached row. Defaults to `id`. */
  key?: keyof T;
  /**
   * Insert a modified row the list has not seen yet. Lists whose rows can be
   * created without an INSERT reaching this client (e.g. an invoice generated
   * by a subscription cycle) need this to stay complete.
   */
  insertMissingOnModify?: boolean;
}

/**
 * Applies a websocket entity event to an RTK Query list cache draft in place.
 * A missing draft means the query is not subscribed, so there is nothing to
 * patch.
 */
export const updateListCache = <T extends { id: string }>(
  event: Events,
  items: T[],
  draft: T[] | undefined,
  { key = 'id', insertMissingOnModify = false }: ListCacheOptions<T> = {},
) => {
  if (!draft) {
    return;
  }

  items.forEach((incoming) => {
    const index = draft.findIndex((item) => item[key] === incoming[key]);

    switch (event) {
      case Events.Insert:
        if (index === -1) {
          draft.unshift(incoming);
        }
        break;
      case Events.Modify:
        if (index !== -1) {
          draft.splice(index, 1, incoming);
        } else if (insertMissingOnModify) {
          draft.unshift(incoming);
        }
        break;
      case Events.Remove:
        if (index !== -1) {
          draft.splice(index, 1);
        }
        break;
      case Events.Refresh:
      case Events.DomainVerified:
        break;
      default:
        ensureUnreachable(event);
    }
  });
};
